import type { CSSProperties } from "react";

/** 平台首页：页面根容器 */
export interface PlatformHomePageConfig {
  /** 顶部内边距（px），不含 safe-area */
  paddingTop: number;
  /** 底部内边距（px），不含 safe-area */
  paddingBottom: number;
  /** 页面背景色 */
  background: string;
}

/** 平台首页：元素布局（位置 + 宽高） */
export interface PlatformHomeElementLayout {
  /** 宽度；支持 px 数字或 CSS 长度字符串（如 "100%"） */
  width: number | string;
  /** 高度；"auto" 表示由内容撑开 */
  height: number | "auto";
  /** 最大宽度（px）；0 表示不限制 */
  maxWidth: number;
  /** 距上方元素间距（px） */
  marginTop: number;
  /** 距下方元素间距（px） */
  marginBottom: number;
  /** 水平内边距（px）；仅容器类元素使用 */
  paddingX: number;
}

/** 平台首页：顶部标语（相对左上角定位） */
export interface PlatformHomeTaglineConfig {
  /** 距左上角垂直偏移（px，已含 page 顶部内边距与 safe-area） */
  offsetTop: number;
  /** 距左边缘（px） */
  offsetLeft: number;
  fontSize: number;
  color: string;
}

/** 平台首页：品牌行（logo + 中文名） */
export interface PlatformHomeBrandRowConfig extends PlatformHomeElementLayout {
  /** logo 与 wordmark 间距（px） */
  gap: number;
}

/** 平台首页：圆形 logo */
export interface PlatformHomeLogoConfig {
  width: number;
  height: number;
  borderRadius: string;
}

/** 平台首页：中文品牌字标 */
export interface PlatformHomeWordmarkConfig {
  width: "auto" | number;
  height: number;
}

/** 平台首页：输入区外层 */
export interface PlatformHomeFormConfig extends PlatformHomeElementLayout {}

/** 中间整体：logo + 输入区 + 人物墙 + 英文 LONGFANS（platform-home-main） */
export interface PlatformHomeMainConfig {
  /** 整体顶部相对整页高度的垂直位置（%）；0=贴顶，100=贴底 */
  topPercent: number;
}

/** 平台首页：输入卡片面板 */
export interface PlatformHomeInputPanelConfig extends PlatformHomeElementLayout {
  paddingTop: number;
  paddingBottom: number;
  borderRadius: number;
  background: string;
}

/** 平台首页：空间 ID 输入说明文案 */
export interface PlatformHomeInputLabelConfig {
  fontSize: number;
  color: string;
  marginBottom: number;
}

/** 平台首页：8 位数字格子 */
export interface PlatformHomeIdCellsConfig {
  gap: number;
  cellHeight: number;
  borderRadius: number;
  background: string;
  fontSize: number;
  color: string;
  borderColor: string;
  activeBorderColor: string;
  errorBorderColor: string;
}

/** @deprecated 保留类型占位，布局已改为 idCells */
export interface PlatformHomeInputConfig {
  width: string;
  height: number;
  fontSize: number;
  letterSpacing: string;
  placeholderFontSize: number;
  placeholderLetterSpacing: string;
  color: string;
  placeholderColor: string;
  errorColor: string;
}

/** 平台首页：「为我推荐」 */
export interface PlatformHomeRandomEnterConfig extends PlatformHomeElementLayout {
  paddingTop: number;
  fontSize: number;
  color: string;
  activeColor: string;
  disabledOpacity: number;
}

/** 平台首页：入口错误提示 */
export interface PlatformHomeEntranceErrorConfig extends PlatformHomeElementLayout {
  fontSize: number;
  color: string;
}

/** 平台首页：人物墙容器 */
export interface PlatformHomeStarwallWrapConfig extends PlatformHomeElementLayout {}

/** 人物墙垂直渐隐：A～D 四个控制点（相对图片高度，自上而下） */
export interface PlatformHomeStarwallFadeMask {
  /** A：图片最高点（%），通常为 0 */
  pointA: number;
  /** B：不透明区下沿 / 透明渐变开始（%）；A～B 为 100% 不透明 */
  pointB: number;
  /** C：全透明区上沿（%）；B～C 线性渐隐，C～D 为全透明 */
  pointC: number;
  /** D：图片最低点（%），通常为 100 */
  pointD: number;
}

/** 平台首页：人物墙图片 + 渐隐 */
export interface PlatformHomeStarwallConfig {
  width: string;
  maxWidth: number;
  height: "auto";
  fadeMask: PlatformHomeStarwallFadeMask;
}

/** 平台首页：英文 LONGFANS 图容器 */
export interface PlatformHomeLongfansWrapConfig extends PlatformHomeElementLayout {}

/** 平台首页：LONGFANS 艺术字 */
export interface PlatformHomeLongfansImgConfig {
  width: string;
  maxWidth: number;
  height: "auto";
}

/** 平台首页：底部标语（相对页面底部定位） */
export interface PlatformHomeSloganConfig {
  /** 距页面底部的距离（px，已含 page 底部内边距与 safe-area） */
  offsetFromBottom: number;
  paddingX: number;
  fontSize: number;
  color: string;
  letterSpacing: string;
  fontFamily: string;
}

/**
 * 平台首页各元素布局配置（逐项 JSON，便于单独调整）
 *
 * 页面三层布局：
 * ┌─────────────────────────────────────┐
 * │ tagline       左上角标语（绝对定位）  │
 * │ main          中间整体（topPercent）  │
 * │   brandRow    logo + 「龙粉社」      │
 * │   form        输入卡片               │
 * │   starwall    人物墙                 │
 * │   longfans    英文 LONGFANS 图       │
 * │ slogan        底部标语（距底 px）    │
 * └─────────────────────────────────────┘
 */
export const PLATFORM_HOME_LAYOUT = {
  /** 整页：背景色、上下内边距（safe-area 在此基础上叠加） */
  page: {
    paddingTop: 20,
    paddingBottom: 20,
    background: "#000",
  },

  /** 左上角标语：「财经资讯和知识分享平台」 */
  tagline: {
    offsetTop: 0,
    offsetLeft: 20,
    fontSize: 14,
    color: "rgba(255, 255, 255, 0.88)",
  },

  /** 中间整体顶部垂直位置（%） */
  main: {
    topPercent: 28,
  },

  /** 品牌行：logo + 「龙粉社」 */
  brandRow: {
    width: "100%",
    height: "auto",
    maxWidth: 0,
    marginTop: 0,
    marginBottom: 56,
    paddingX: 20,
    gap: 20,
  },

  /** 品牌行内：圆形 logo（logo.png） */
  logo: {
    width: 52,
    height: 52,
    borderRadius: "50%",
  },

  /** 品牌行内：「龙粉社」中文白色字标（chinese-webname-white.png） */
  wordmark: {
    width: "auto",
    height: 32,
  },

  /** 主内容区：输入卡片 + 人物墙 + 英文 LONGFANS */
  form: {
    width: "85%",
    height: "auto",
    maxWidth: 0,
    marginTop: 0,
    marginBottom: 0,
    paddingX: 0,
  },

  /** 输入区：深灰圆角卡片容器 */
  inputPanel: {
    width: "100%",
    height: "auto",
    maxWidth: 0,
    marginTop: 0,
    marginBottom: 0,
    paddingX: 0,
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: 0,
    background: "transparent",
  },

  /** 输入区第一行：说明文案 */
  inputLabel: {
    fontSize: 12,
    color: "rgba(255, 255, 255, 0.55)",
    marginBottom: 10,
  },

  /** 输入区第二行：8 位数字格子 */
  idCells: {
    gap: 6,
    cellHeight: 40,
    borderRadius: 3,
    background: "#1a1a1a",
    fontSize: 20,
    color: "#ffffff",
    borderColor: "rgba(255, 255, 255, 0.12)",
    activeBorderColor: "rgba(255, 255, 255, 0.45)",
    errorBorderColor: "#ff7875",
  },

  /** @deprecated 已由 idCells 替代 */
  input: {
    width: "100%",
    height: 44,
    fontSize: 22,
    letterSpacing: "0.28em",
    placeholderFontSize: 22,
    placeholderLetterSpacing: "0.04em",
    color: "#fff",
    placeholderColor: "rgba(255, 255, 255, 0.38)",
    errorColor: "#ff7875",
  },

  /** 输入区第三行：「为我推荐」 */
  randomEnter: {
    width: "100%",
    height: "auto",
    maxWidth: 0,
    marginTop: 12,
    marginBottom: 0,
    paddingX: 0,
    paddingTop: 0,
    fontSize: 15,
    color: "#7ec8f8",
    activeColor: "#a8dcff",
    disabledOpacity: 0.4,
  },

  /** 卡片内：空间 ID 校验失败时的红色提示文案 */
  entranceError: {
    width: "100%",
    height: "auto",
    maxWidth: 0,
    marginTop: 10,
    marginBottom: 0,
    paddingX: 0,
    fontSize: 13,
    color: "#ff7875",
  },

  /** 人物墙：marginTop = 输入卡片底部到人物墙顶部间距 */
  starwallWrap: {
    width: "100%",
    height: "auto",
    maxWidth: 0,
    marginTop: 32,
    marginBottom: 0,
    paddingX: 0,
  },

  /** 人物墙图片 starwall.png；fadeMask 四点控制垂直渐隐 */
  starwall: {
    width: "70%",
    maxWidth: 520,
    height: "auto",
    fadeMask: {
      pointA: 0,
      pointB: 50,
      pointC: 65,
      pointD: 100,
    },
  },

  /** 英文 LONGFANS 图；marginTop 负值可上移靠近人物墙 */
  longfansWrap: {
    width: "70%",
    height: "auto",
    maxWidth: 0,
    marginTop: -50,
    marginBottom: 0,
    paddingX: 20,
  },

  /** 粉色 LONGFANS 艺术字（longfans.png） */
  longfansImg: {
    width: "100%",
    maxWidth: 300,
    height: "auto",
  },

  /** 底部标语「与远见者，长期同行」；offsetFromBottom = 距页面底部距离 */
  slogan: {
    offsetFromBottom: 16,
    paddingX: 20,
    fontSize: 17,
    color: "#62d4fa",
    letterSpacing: "0.06em",
    fontFamily: 'SimSun, 宋体, serif',
  },
} as const satisfies {
  page: PlatformHomePageConfig;
  tagline: PlatformHomeTaglineConfig;
  main: PlatformHomeMainConfig;
  brandRow: PlatformHomeBrandRowConfig;
  logo: PlatformHomeLogoConfig;
  wordmark: PlatformHomeWordmarkConfig;
  form: PlatformHomeFormConfig;
  inputPanel: PlatformHomeInputPanelConfig;
  inputLabel: PlatformHomeInputLabelConfig;
  idCells: PlatformHomeIdCellsConfig;
  input: PlatformHomeInputConfig;
  randomEnter: PlatformHomeRandomEnterConfig;
  entranceError: PlatformHomeEntranceErrorConfig;
  starwallWrap: PlatformHomeStarwallWrapConfig;
  starwall: PlatformHomeStarwallConfig;
  longfansWrap: PlatformHomeLongfansWrapConfig;
  longfansImg: PlatformHomeLongfansImgConfig;
  slogan: PlatformHomeSloganConfig;
};

function length(v: number | string): string {
  return typeof v === "number" ? `${v}px` : v;
}

function maxWidth(v: number): string {
  return v > 0 ? `${v}px` : "none";
}

/** 平台首页布局 CSS 变量（由 Config 注入到根节点） */
export function platformHomeLayoutCssVars(): CSSProperties {
  const {
    page,
    tagline,
    main,
    brandRow,
    logo,
    wordmark,
    form,
    inputPanel,
    inputLabel,
    idCells,
    input,
    randomEnter,
    entranceError,
    starwallWrap,
    starwall,
    longfansWrap,
    longfansImg,
    slogan,
  } = PLATFORM_HOME_LAYOUT;

  return {
    ["--platform-home-page-padding-top" as string]: `${page.paddingTop}px`,
    ["--platform-home-page-padding-bottom" as string]: `${page.paddingBottom}px`,
    ["--platform-home-page-bg" as string]: page.background,

    ["--platform-home-tagline-offset-top" as string]: `${tagline.offsetTop}px`,
    ["--platform-home-tagline-offset-left" as string]: `${tagline.offsetLeft}px`,
    ["--platform-home-tagline-font-size" as string]: `${tagline.fontSize}px`,
    ["--platform-home-tagline-color" as string]: tagline.color,

    ["--platform-home-main-top-percent" as string]: `${main.topPercent}%`,

    ["--platform-home-brand-row-width" as string]: length(brandRow.width),
    ["--platform-home-brand-row-margin-top" as string]: `${brandRow.marginTop}px`,
    ["--platform-home-brand-row-margin-bottom" as string]: `${brandRow.marginBottom}px`,
    ["--platform-home-brand-row-padding-x" as string]: `${brandRow.paddingX}px`,
    ["--platform-home-brand-row-gap" as string]: `${brandRow.gap}px`,

    ["--platform-home-logo-width" as string]: `${logo.width}px`,
    ["--platform-home-logo-height" as string]: `${logo.height}px`,
    ["--platform-home-logo-radius" as string]: logo.borderRadius,

    ["--platform-home-wordmark-width" as string]:
      wordmark.width === "auto" ? "auto" : `${wordmark.width}px`,
    ["--platform-home-wordmark-height" as string]: `${wordmark.height}px`,

    ["--platform-home-form-width" as string]: length(form.width),
    ["--platform-home-form-max-width" as string]: maxWidth(form.maxWidth),
    ["--platform-home-form-margin-top" as string]: `${form.marginTop}px`,
    ["--platform-home-form-margin-bottom" as string]: `${form.marginBottom}px`,
    ["--platform-home-form-padding-x" as string]: `${form.paddingX}px`,

    ["--platform-home-input-panel-width" as string]: length(inputPanel.width),
    ["--platform-home-input-panel-padding-top" as string]: `${inputPanel.paddingTop}px`,
    ["--platform-home-input-panel-padding-bottom" as string]: `${inputPanel.paddingBottom}px`,
    ["--platform-home-input-panel-padding-x" as string]: `${inputPanel.paddingX}px`,
    ["--platform-home-input-panel-radius" as string]: `${inputPanel.borderRadius}px`,
    ["--platform-home-input-panel-bg" as string]: inputPanel.background,

    ["--platform-home-input-label-font-size" as string]: `${inputLabel.fontSize}px`,
    ["--platform-home-input-label-color" as string]: inputLabel.color,
    ["--platform-home-input-label-margin-bottom" as string]: `${inputLabel.marginBottom}px`,

    ["--platform-home-id-cells-gap" as string]: `${idCells.gap}px`,
    ["--platform-home-id-cell-height" as string]: `${idCells.cellHeight}px`,
    ["--platform-home-id-cell-radius" as string]: `${idCells.borderRadius}px`,
    ["--platform-home-id-cell-bg" as string]: idCells.background,
    ["--platform-home-id-cell-font-size" as string]: `${idCells.fontSize}px`,
    ["--platform-home-id-cell-color" as string]: idCells.color,
    ["--platform-home-id-cell-border-color" as string]: idCells.borderColor,
    ["--platform-home-id-cell-active-border-color" as string]: idCells.activeBorderColor,
    ["--platform-home-id-cell-error-border-color" as string]: idCells.errorBorderColor,

    ["--platform-home-input-width" as string]: input.width,
    ["--platform-home-input-height" as string]: `${input.height}px`,
    ["--platform-home-input-font-size" as string]: `${input.fontSize}px`,
    ["--platform-home-input-letter-spacing" as string]: input.letterSpacing,
    ["--platform-home-input-placeholder-font-size" as string]: `${input.placeholderFontSize}px`,
    ["--platform-home-input-placeholder-letter-spacing" as string]:
      input.placeholderLetterSpacing,
    ["--platform-home-input-color" as string]: input.color,
    ["--platform-home-input-placeholder-color" as string]: input.placeholderColor,
    ["--platform-home-input-error-color" as string]: input.errorColor,

    ["--platform-home-random-enter-width" as string]: length(randomEnter.width),
    ["--platform-home-random-enter-margin-top" as string]: `${randomEnter.marginTop}px`,
    ["--platform-home-random-enter-padding-top" as string]: `${randomEnter.paddingTop}px`,
    ["--platform-home-random-enter-font-size" as string]: `${randomEnter.fontSize}px`,
    ["--platform-home-random-enter-color" as string]: randomEnter.color,
    ["--platform-home-random-enter-active-color" as string]: randomEnter.activeColor,
    ["--platform-home-random-enter-disabled-opacity" as string]: String(
      randomEnter.disabledOpacity
    ),

    ["--platform-home-entrance-error-width" as string]: length(entranceError.width),
    ["--platform-home-entrance-error-margin-top" as string]: `${entranceError.marginTop}px`,
    ["--platform-home-entrance-error-font-size" as string]: `${entranceError.fontSize}px`,
    ["--platform-home-entrance-error-color" as string]: entranceError.color,

    ["--platform-home-starwall-wrap-width" as string]: length(starwallWrap.width),
    ["--platform-home-starwall-wrap-margin-top" as string]: `${starwallWrap.marginTop}px`,
    ["--platform-home-starwall-wrap-margin-bottom" as string]: `${starwallWrap.marginBottom}px`,

    ["--platform-home-starwall-width" as string]: starwall.width,
    ["--platform-home-starwall-max-width" as string]: `${starwall.maxWidth}px`,

    ["--platform-home-longfans-wrap-width" as string]: length(longfansWrap.width),
    ["--platform-home-longfans-wrap-margin-top" as string]: `${longfansWrap.marginTop}px`,
    ["--platform-home-longfans-wrap-padding-x" as string]: `${longfansWrap.paddingX}px`,

    ["--platform-home-longfans-width" as string]: longfansImg.width,
    ["--platform-home-longfans-max-width" as string]: `${longfansImg.maxWidth}px`,

    ["--platform-home-slogan-offset-from-bottom" as string]: `${slogan.offsetFromBottom}px`,
    ["--platform-home-slogan-padding-x" as string]: `${slogan.paddingX}px`,
    ["--platform-home-slogan-font-size" as string]: `${slogan.fontSize}px`,
    ["--platform-home-slogan-color" as string]: slogan.color,
    ["--platform-home-slogan-letter-spacing" as string]: slogan.letterSpacing,
    ["--platform-home-slogan-font-family" as string]: slogan.fontFamily,
  };
}

/** 根据 fadeMask 四点生成 mask 渐变（使用 rgba，避免 transparent 在 WebKit 插值异常） */
export function buildStarwallFadeMaskGradient(
  mask: PlatformHomeStarwallFadeMask = PLATFORM_HOME_LAYOUT.starwall.fadeMask
): string {
  const { pointA, pointB, pointC, pointD } = mask;
  return `linear-gradient(to bottom, rgba(255,255,255,1) ${pointA}%, rgba(255,255,255,1) ${pointB}%, rgba(255,255,255,0) ${pointC}%, rgba(255,255,255,0) ${pointD}%)`;
}

/** 人物墙渐隐 mask 样式（inline 注入，避免 CSS 变量写在 mask-image 里失效） */
export function platformHomeStarwallMaskStyle(): CSSProperties {
  const gradient = buildStarwallFadeMaskGradient();
  return {
    WebkitMaskImage: gradient,
    maskImage: gradient,
    WebkitMaskSize: "100% 100%",
    maskSize: "100% 100%",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
  };
}
